import React, { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bug,
  CalendarDays,
  ChevronDown,
  Database,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Settings,
  X,
} from "lucide-react";
import { cn } from "./ui/cn";
import { isPlatformAdmin } from "../shared/utils/account-role";

const DRAWER_WIDTH = 260;

const NAV_SECTIONS = [
  {
    label: "Analytics",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Overview" },
      { to: "/publish", icon: CalendarDays, label: "Publish" },
      { to: "/reports", icon: BarChart3, label: "Report builder" },
      { to: "/integrations", icon: Plug, label: "Data sources" },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/meta-api-test", icon: Bug, label: "Meta API test" },
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
];

const ROADMAP_ITEMS = [
  { icon: Database, label: "Destinations" },
];

function NavLink({ item, pathname, onNavigate }) {
  const Icon = item.icon;
  const selected =
    item.to === "/" ? pathname === "/" : pathname.startsWith(item.to);

  return (
    <RouterLink
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-[0.9375rem] leading-relaxed transition-colors",
        selected
          ? "bg-white/10 font-semibold text-white shadow-sm ring-1 ring-white/10"
          : "font-medium text-slate-400 hover:bg-white/5 hover:text-slate-200"
      )}
    >
      <Icon
        className={cn(
          "h-[18px] w-[18px] shrink-0",
          selected ? "text-brand-light" : "text-slate-500 group-hover:text-slate-300"
        )}
        strokeWidth={selected ? 2.25 : 2}
      />
      {item.label}
    </RouterLink>
  );
}

function sectionsFor(account) {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => item.to !== "/meta-api-test" || isPlatformAdmin(account),
    ),
  })).filter((section) => section.items.length > 0);
}

function SidebarContent({ pathname, onNavigate, account }) {
  const sections = sectionsFor(account);
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="px-5 pb-5 pt-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-indigo-400 to-brand shadow-lg shadow-indigo-500/30">
            <BarChart3 className="h-[18px] w-[18px] text-white" strokeWidth={2.25} />
          </div>
          <div>
            <p className="text-[0.975rem] font-bold leading-snug tracking-tight text-slate-100">
              OrkaAnalytics
            </p>
            <p className="text-[0.65rem] tracking-wide text-slate-500">
              Marketing platform
            </p>
          </div>
        </div>
      </div>

      <div className="mx-4 border-t border-sidebar-border" />

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((section) => (
          <div key={section.label} className="mb-4">
            <p className="mb-2 px-3 text-[0.65rem] font-bold uppercase tracking-[0.09em] text-slate-600">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  item={item}
                  pathname={pathname}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-4 py-4">
        <p className="mb-2 px-1 text-[0.65rem] font-bold uppercase tracking-[0.08em] text-slate-600">
          On the roadmap
        </p>
        <div className="mb-3 space-y-1">
          {ROADMAP_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.label}
                className="flex items-center gap-2 px-1 py-1 opacity-50"
              >
                <Icon className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-[0.78rem] font-medium text-slate-500">
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
        <p className="px-1 text-[0.65rem] text-slate-600">v1.0 · Beta</p>
      </div>
    </div>
  );
}

export default function AppShell({ account, onSignOut, title, actions, children, workspace = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = (account?.displayName || account?.email || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const closeMobile = () => setMobileOpen(false);

  return (
    <div
      className={cn(
        "flex min-h-screen bg-canvas",
        workspace && "h-screen overflow-hidden"
      )}
    >
      {/* Desktop sidebar */}
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden w-[260px] flex-col bg-sidebar md:flex"
        style={{ width: DRAWER_WIDTH }}
      >
        <SidebarContent pathname={location.pathname} account={account} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeMobile}
          />
          <aside className="relative flex h-full w-[260px] flex-col bg-sidebar shadow-2xl">
            <button
              type="button"
              onClick={closeMobile}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent pathname={location.pathname} onNavigate={closeMobile} account={account} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col",
          workspace && "min-h-0"
        )}
      >
        <div
          className={cn(
            "flex flex-col md:pl-[260px]",
            workspace && "min-h-0 flex-1"
          )}
        >
          <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
            <div className="flex h-14 items-center gap-3 px-4 md:px-6">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>

              <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-slate-900 md:text-lg">
                {title}
              </h1>

              <div className="flex items-center gap-2">
                {actions}

                {account ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setMenuOpen((v) => !v)}
                      className="flex items-center gap-2 rounded-xl border border-transparent px-1.5 py-1 transition-colors hover:border-slate-200 hover:bg-slate-50"
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-xs font-bold text-white ring-2 ring-indigo-100">
                        {initials}
                      </span>
                      <span className="hidden text-sm font-semibold text-slate-800 sm:block">
                        {account.displayName?.split(" ")[0] || "Account"}
                      </span>
                      <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" />
                    </button>

                    {menuOpen && (
                      <>
                        <button
                          type="button"
                          aria-label="Close account menu"
                          className="fixed inset-0 z-40"
                          onClick={() => setMenuOpen(false)}
                        />
                        <div className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                          <div className="border-b border-slate-100 px-4 py-3">
                            <p className="text-sm font-semibold text-slate-900">
                              {account.displayName || "Account"}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              {account.email}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              navigate("/settings");
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <Settings className="h-4 w-4" />
                            Settings
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              if (onSignOut) onSignOut();
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                          >
                            <LogOut className="h-4 w-4" />
                            Sign out
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/signin")}
                    className="text-sm font-semibold text-brand hover:text-indigo-700"
                  >
                    Sign in
                  </button>
                )}
              </div>
            </div>
          </header>

          <main
            className={cn(
              "w-full",
              workspace
                ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                : "mx-auto max-w-[1440px] overflow-x-hidden px-4 py-6 md:px-8 md:py-8"
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
